import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import type {
  EAccessLevel,
  EBaseAccess,
  ECapability,
  EInternalAdminCapability,
} from '~/features/user/models'

import { FIVE_MINUTES_IN_MS } from '~/constants'
import useHasValidAuthSession from '~/features/auth/hooks/useHasValidAuthSession'
import useIsAdmin from '~/features/campaigns/hooks/useIsAdmin'
import { queryKeys } from '~/features/user/utils'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import { store } from '~/store'
import pathBuilder from '~/utils/api'

const getUserPermissionsByCognitoId = async ({
  cognitoId,
}: {
  cognitoId: string
}): Promise<TMyPermissions | null> => {
  const response = await HttpService.get<TMyPermissions>(
    {
      path: pathBuilder(EServerPath.apiUsersPermissionsByCognitoId, {
        cognitoId,
      }),
    },
    EHttpService.internal,
  )

  return response.data ?? null
}

const getMyUserPermissions = async (): Promise<TMyPermissions | null> => {
  const response = await HttpService.get<TMyPermissions>(
    { path: EServerPath.apiUsersMePermissions },
    EHttpService.internal,
  )

  return response.data ?? null
}

type TMyPermissions = {
  baseAccess: EBaseAccess
  capabilities: Partial<Record<ECapability, EAccessLevel>>
  orgId: string
  // Only populated for admin/adminLimited callers — null for everyone else.
  internalAdmin: {
    grantsAll: boolean
    capabilities: Partial<Record<EInternalAdminCapability, EAccessLevel>>
  } | null
}

type TQueryOptions = Omit<
  UseQueryOptions<TMyPermissions | null>,
  'queryFn' | 'queryKey'
>

type TProps = {
  options?: TQueryOptions
}

const useGetMyPermissions = ({ options } = {} as TProps) => {
  const { hasValidAuthSession } = useHasValidAuthSession()
  const { enabled: optionsEnabled, ...restOptions } = options || {}

  const [{ id: userId, impersonatorsUserId }, recentLoginAsUsers] = store(
    (state) => [state.user, state.adminPortal.recentLoginAsUsers],
  )

  const isAdmin = useIsAdmin()

  const cognitoId = recentLoginAsUsers?.find(
    (user) => user.id === userId,
  )?.cognitoUid

  const queryFn =
    isAdmin && impersonatorsUserId && cognitoId
      ? () => getUserPermissionsByCognitoId({ cognitoId })
      : getMyUserPermissions

  return useQuery({
    enabled: hasValidAuthSession && (optionsEnabled ?? true),
    queryKey: queryKeys.myPermissions(userId),
    queryFn,
    staleTime: FIVE_MINUTES_IN_MS,
    // No retries — a 404 means the backend endpoint isn't deployed yet.
    // Treat any error as "no custom access" rather than looping.
    retry: false,
    ...restOptions,
  })
}

export default useGetMyPermissions
